import {
  Card,
  Table,
  Tag,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Avatar,
} from "antd";

import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import useDebounce from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";
import apiPath from "../../constants/apiPath";

const { RangePicker } = DatePicker;
const { Option } = Select;

const RewardManager = () => {
  const [form] = Form.useForm();
  const [list, setList] = useState([]);
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);
  const [filterValues, setFilterValues] = useState({
    status: null,
    start_date: null,
    end_date: null,
  });

  const api = {
    list: apiPath.RedemptionHistoryList,
  };

  const columns = [
    {
      title: "User",
      dataIndex: ["user", "name"],
      key: "userName",
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar src={record?.user?.image || null} style={{ marginRight: 8 }}>
            {name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: "bold" }}>{name}</div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {record?.user?.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Reward Title",
      dataIndex: ["voucher", "title"],
      key: "rewardTitle",
    },
    {
      title: "Reward Code",
      dataIndex: ["voucher", "code"],
      key: "rewardCode",
    },
    {
      title: "Redeemed Date",
      dataIndex: "redeemedAt",
      key: "redeemedAt",
      render: (date) => moment(date).format("DD MMM YYYY, h:mm A"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color =
          status === "Redeemed"
            ? "green"
            : status === "Expired"
            ? "red"
            : "blue";
        return <Tag color={color}>{status}</Tag>;
      },
    },
  ];

  const fetchData = () => {
    setLoading(true);

    const queryParams = new URLSearchParams({
      page: pagination.current,
      pageSize: pagination.pageSize,
    });

    if (filterValues.status) {
      queryParams.append("status", filterValues.status);
    }
    if (filterValues.start_date) {
      queryParams.append("start_date", filterValues.start_date);
    }
    if (filterValues.end_date) {
      queryParams.append("end_date", filterValues.end_date);
    }

    request({
      url: `${
        api.list
      }?${queryParams.toString()}&search=${debouncedSearchText}&start_date=${
        startDate ? startDate : ""
      }&end_date=${endDate ? endDate : ""}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setList(data?.list?.docs || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.list?.totalDocs || 0,
          current: data?.list?.page || 1,
          pageSize: data?.list?.limit || 10,
        }));
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchData();
  }, [
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
    JSON.stringify(filterValues),
    startDate,
    endDate,
  ]);

  const onFilterSubmit = (values) => {
    const { search, dateRange, status } = values;

    setSearchText(search || "");

    setFilterValues({
      status: status || null,
      start_date: dateRange ? dateRange[0].toISOString() : null,
      end_date: dateRange ? dateRange[1].toISOString() : null,
    });

    setPagination((p) => ({ ...p, current: 1 }));
  };

  return (
    <Card
      title="Reward Manager - Distribution History"
      extra={
        <Form
          form={form}
          layout="inline"
          onFinish={onFilterSubmit}
          initialValues={{ search: "", status: null, dateRange: null }}
        >
          <div className="w-100 d-flex align-items-baseline text-head_right_cont">
            <div className="pageHeadingSearch d-flex gap-2">
              <Form.Item name="search">
                <Input.Search
                  placeholder="Search by User, reward title"
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  className="searchInput"
                />
              </Form.Item>

              <div className="role-wrap">
                <DatePicker.RangePicker
                  format="DD-MM-YYYY"
                  onChange={(dates) => {
                    setStartDate(dates?.[0]?.format("YYYY-MM-DD"));
                    setEndDate(dates?.[1]?.format("YYYY-MM-DD"));
                  }}
                  className="filter-date-range filter-date-range-for-width"
                />
              </div>

              <Form.Item name="status">
                <Select allowClear placeholder="Status" style={{ width: 150 }}>
                  <Option value="Redeemed">Redeemed</Option>
                  <Option value="Expired">Expired</Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Apply
                </Button>
              </Form.Item>
            </div>
          </div>
        </Form>
      }
    >
      <div className="table-responsive customPagination">
        <Table
          columns={columns}
          dataSource={list}
          loading={loading}
          rowKey="_id"
          pagination={pagination}
          onChange={(paginationConfig) => setPagination(paginationConfig)}
        />
      </div>
    </Card>
  );
};

export default RewardManager;
